import { LinearProgress } from '@mui/material';
import { lazy, Suspense } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';

import { Layout } from '../components/Layout';
import { useProvideAuth } from '../hooks/useProvideAuth';

// Each page is its own chunk, so opening one page no longer downloads all of them.
const lazyPage = (load, exportName) =>
	lazy(() => load().then((module) => ({ default: exportName ? module[exportName] : module.default })));

const BetToolPage = lazyPage(() => import('./BetToolPage'), 'BetToolPage');
const ExchangeRatePage = lazyPage(() => import('./ExchangeRatePage'), 'ExchangeRatePage');
const OrderSettingPage = lazyPage(() => import('./gameTemplate/OrderSettingPage'), 'OrderSettingPage');
const GameInfoManagePage = lazyPage(() => import('./GameInfoManagePage'), 'GameInfoManagePage');
const GameCertFilesPage = lazyPage(() => import('./GameCertFilesPage'), 'GameCertFilesPage');
const GameProviderInfoPage =lazyPage(() => import('./GameProviderInfoPage'), 'GameProviderInfoPage');
const TemplateManagePage = lazyPage(() => import('./gameTemplate/TemplateManagePage'), 'TemplateManagePage');
const OperatorSettingPage = lazyPage(() => import('./gameTemplate/OperatorSettingPage'), 'OperatorSettingPage');
const TemplateListPage = lazyPage(() => import('./gameTemplate/TemplateListPage'));
const HomePage = lazyPage(() => import('./HomePage'), 'HomePage');
const BannerManagePage = lazyPage(() => import('./lobby/BannerManagePage'), 'BannerManagePage');
const GameTogglePage = lazyPage(() => import('./lobby/GameTogglePage'), 'GameTogglePage');
const SettingTemplatePage = lazyPage(() => import('./lobby/SettingTemplatePage'), 'SettingTemplatePage');
const TopOneManagePage = lazyPage(() => import('./lobby/TopOneManagePage'), 'TopOneManagePage');
const UpdateSettingTemplatePage = lazyPage(() => import('./lobby/UpdateSettingTemplatePage'), 'UpdateSettingTemplatePage');
const LoginPage = lazyPage(() => import('./LoginPage'), 'LoginPage');
const MaintainHistoryPage = lazyPage(() => import('./MaintainHistoryPage'), 'MaintainHistoryPage');
const ManageBetListPage = lazyPage(() => import('./ManageBetListPage'), 'ManageBetListPage');
const OperatorOnboardPage = lazyPage(() => import('./OperatorOnboardPage'), 'OperatorOnboardPage');
const OperatorInfoPage = lazyPage(() => import('./OperatorInfoPage'), 'OperatorInfoPage');
const SearchRunningBetPage = lazyPage(() => import('./SearchRunningBetPage'), 'SearchRunningBetPage');
const SubLineInfosPage = lazyPage(() => import('./SubLineInfosPage'));
const SubLineRequisitionsPage = lazyPage(() => import('./SubLineRequisitionsPage'));
const MappingListPage = lazyPage(() => import('./gameTemplate/MappingListPage'), 'MappingListPage');
const GameSettingPage = lazyPage(() => import('./gameTemplate/GameSettingPage'), 'GameSettingPage');
const MappingManagePage = lazyPage(() => import('./gameTemplate/MappingManagePage'), 'MappingManagePage');
const OpGameListPage = lazyPage(() => import('./gameTemplate/OpGameListPage'));
const OpLobbyGameListPage = lazyPage(() => import('./gameTemplate/OpLobbyGameListPage'));
const PlayerRiskLevelsPage = lazyPage(() => import('./PlayerRiskLevelsPage'), 'PlayerRiskLevelsPage');
const GameTotalRiskLevelsPage = lazyPage(() => import('./GameTotalRiskLevelsPage'), 'GameTotalRiskLevelsPage');
const GameRiskMappingPage = lazyPage(() => import('./GameRiskMappingPage'), 'GameRiskMappingPage');
const RiskBlacklistPage = lazyPage(() => import('./RiskBlacklistPage'), 'RiskBlacklistPage');
const VoucherEventPage = lazyPage(() => import('./VoucherEventPage'), 'VoucherEventPage');
const CacheInfoPage = lazyPage(() => import('./settingsCache/CacheInfoPage'), 'CacheInfoPage');
const OperatorFamilyPage = lazyPage(() => import('./settingsCache/OperatorFamilyPage'), 'OperatorFamilyPage');

const elements = {
	home: <HomePage />,
	operatorInfo: <OperatorInfoPage />,
	operatorOnboard: <OperatorOnboardPage />,
	gameProviderInfo: <GameProviderInfoPage />,
	searchRunningBet: <SearchRunningBetPage />,
	manageBetList: <ManageBetListPage />,
	maintainHistory: <MaintainHistoryPage />,
	lobbySettingTemplate: <SettingTemplatePage />,
	'lobbySettingTemplate/:settingName': <UpdateSettingTemplatePage />,
	lobbyGameToggle: <GameTogglePage />,
	'lobbyBannerManage/:settingName': <BannerManagePage />,
	'lobbyTopOneManage/:settingName': <TopOneManagePage />,
	exchangeRate: <ExchangeRatePage />,
	subLineRequisitions: <SubLineRequisitionsPage />,
	subLineInfos: <SubLineInfosPage />,
	orderSetting: <OrderSettingPage />,
	gameListTemplate: <TemplateListPage />,
	'gameListTemplate/manage': <TemplateManagePage />,
	operatorSetting: <OperatorSettingPage />,
	gameTypeMapping: <MappingListPage />,
	'gameTypeMapping/manage': <MappingManagePage />,
	gameSetting: <GameSettingPage />,
	gameInfoManage: <GameInfoManagePage />,
	gameCertFiles: <GameCertFilesPage />,
	opGameList: <OpGameListPage />,
	lobbyOpGameList: <OpLobbyGameListPage />,
	betTool: <BetToolPage />,
	playerRiskLevels: <PlayerRiskLevelsPage />,
	gameTotalRiskLevels: <GameTotalRiskLevelsPage />,
	gameRiskMapping: <GameRiskMappingPage />,
	riskBlacklist: <RiskBlacklistPage />,
	voucherEvents: <VoucherEventPage />,
	cacheInfo: <CacheInfoPage />,
	operatorFamily: <OperatorFamilyPage />,
};

export function Router() {
	const { isAuth } = useProvideAuth();
	const location = useLocation();

	return (
		<Suspense fallback={<LinearProgress />}>
			<Routes>
				<Route path="/login" element={isAuth ? <Navigate to="/home" /> : <LoginPage />} />
				<Route path="/" element={isAuth ? <Layout /> : <Navigate to="/login" state={{ from: location }} />}>
					{Object.keys(elements).map((path) => (
						<Route path={path} element={elements[path]} key={path} />
					))}
					<Route index element={<Navigate to="/home" />} />
				</Route>
				<Route path="*" element={<Navigate to="/home" />} />
			</Routes>
		</Suspense>
	);
}
